import { appAlert } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
// usage_email.js — "Email upload requests", the one implementation.
//
// WHY ITS OWN FILE
//
// Same reason as usage_grid.js. This dialog existed twice - inline in
// stock_excess.js and again in usage_tools.js - and the two had already parted
// company: the detail screen showed when a branch was last written to and how
// many uploads had arrived since, and the overview's copy showed neither, so
// the same button told you different things depending on which screen you had
// opened it from. Both now import this.
//
// WHERE THE ADDRESS COMES FROM
//
// The address on screen is the one held against the branch on the organisation
// itself, and it is editable here. The branch's user record is only ever read,
// and only to fill in a branch that has never had an address - after that the
// value here is the value, and correcting it does NOT change what the branch
// logs in with.
//
// Props:
//   open       render the dialog
//   onClose    called by Close and by the backdrop
//   groupId    the organisation (localStorage "userId" on both screens)
//   masked     hide branch names for screen sharing

import React, { useState, useEffect } from "react";
import { displayName } from "./nameMask";
const API = apiUrl("");
const token = () => localStorage.getItem("authToken");
export default function UsageEmailModal({
  open,
  onClose,
  groupId,
  masked = false
}) {
  const [recipients, setRecipients] = useState(null);
  const [recipientsBusy, setRecipientsBusy] = useState(false);
  const [requestPreview, setRequestPreview] = useState(null); // {subject, html, hasScreenshot}
  const [sendingTo, setSendingTo] = useState({}); // pharmacyId -> in flight
  const [sentTo, setSentTo] = useState({}); // pharmacyId -> sent in THIS dialogue
  const [drafts, setDrafts] = useState({}); // pharmacyId -> what is in the box
  const [savingEmail, setSavingEmail] = useState({}); // pharmacyId -> in flight

  const fetchRecipients = async () => {
    try {
      setRecipientsBusy(true);
      setSentTo({});
      const res = await fetch(`${API}/usageUploadRecipients`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify({
          groupId
        })
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        (await appAlert(`Could not load the branch list: HTTP ${res.status}${body ? `\n\n${body.slice(0, 300)}` : ""}`));
        return;
      }
      const data = await res.json();
      const list = data.members || [];
      setRecipients(list);
      // Boxes start at the saved value, so "changed" means changed by a person
      // rather than merely typed in.
      setDrafts(Object.fromEntries(list.map(m => [m.pharmacyId, m.email || ""])));
    } catch (e) {
      (await appAlert(`Error loading branches: ${e.message}`));
    } finally {
      setRecipientsBusy(false);
    }
  };

  // Loaded on each open rather than once at mount: "last sent" is the whole
  // point of the table, and a stale one would be read as fact.
  useEffect(() => {
    if (open) {
      setRequestPreview(null);
      fetchRecipients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const isDirty = m => (drafts[m.pharmacyId] ?? "") !== (m.email || "");

  // Saves to the organisation's member list. Nothing here writes to the
  // branch's user record - see the note at the top of this file.
  const saveEmail = async m => {
    const value = (drafts[m.pharmacyId] ?? "").trim();
    if (savingEmail[m.pharmacyId]) return;
    try {
      setSavingEmail(t => ({
        ...t,
        [m.pharmacyId]: true
      }));
      const res = await fetch(`${API}/setUsageUploadRecipientEmail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify({
          groupId,
          pharmacyId: m.pharmacyId,
          email: value
        })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.error) {
        (await appAlert(`${m.pharmacyName}: ${d.error || `HTTP ${res.status}`}`));
        return;
      }
      setRecipients(rs => (rs || []).map(r => r.pharmacyId === m.pharmacyId ? {
        ...r,
        email: d.email || null,
        emailSource: d.emailSource || null
      } : r));
      setDrafts(t => ({
        ...t,
        [m.pharmacyId]: d.email || ""
      }));
      // A changed address makes the tick from a moment ago misleading - it was
      // sent, but not to what the row now shows.
      setSentTo(t => {
        const n = {
          ...t
        };
        delete n[m.pharmacyId];
        return n;
      });
    } catch (e) {
      (await appAlert(`${m.pharmacyName}: ${e.message}`));
    } finally {
      setSavingEmail(t => {
        const n = {
          ...t
        };
        delete n[m.pharmacyId];
        return n;
      });
    }
  };

  // What a branch actually receives. Placeholder link, no token issued.
  const previewRequestEmail = async m => {
    try {
      const res = await fetch(`${API}/previewUsageUploadEmail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify({
          groupId,
          pharmacyId: m?.pharmacyId,
          pharmacyName: m?.pharmacyName
        })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.error) {
        (await appAlert(`Preview failed: ${d.error || `HTTP ${res.status}`}`));
        return;
      }

      // The email embeds the screenshot as a CID attachment, which a browser
      // cannot resolve. Point it at the served copy so the preview shows the
      // same picture the branch will see.
      const html = String(d.html || "").replace(/src="cid:[^"]*"/g, `src="${API}/usage-upload-screenshot"`);
      setRequestPreview({
        subject: d.subject,
        html,
        hasScreenshot: d.hasScreenshot
      });
    } catch (e) {
      (await appAlert(`Preview failed: ${e.message}`));
    }
  };

  // One branch at a time, so each result lands against the branch it belongs to.
  // A single send-to-everyone button gives one answer for a hundred sends, and
  // the one branch whose address is wrong disappears into it.
  const sendRequestTo = async m => {
    if (sendingTo[m.pharmacyId]) return;
    try {
      setSendingTo(t => ({
        ...t,
        [m.pharmacyId]: true
      }));
      const res = await fetch(`${API}/sendUsageUploadRequests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify({
          groupId,
          pharmacyId: m.pharmacyId
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        (await appAlert(`${m.pharmacyName}: ${data.error || `HTTP ${res.status}`}`));
        return;
      }
      setSentTo(t => ({
        ...t,
        [m.pharmacyId]: true
      }));
    } catch (e) {
      (await appAlert(`${m.pharmacyName}: ${e.message}`));
    } finally {
      setSendingTo(t => {
        const n = {
          ...t
        };
        delete n[m.pharmacyId];
        return n;
      });
    }
  };
  if (!open) return null;
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1600
  }}>
      <div style={{
      background: "#fff",
      width: "90%",
      maxWidth: "900px",
      maxHeight: "85%",
      overflow: "auto",
      padding: "16px",
      borderRadius: "4px"
    }}>
        <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
          <h2 style={{
          margin: 0
        }}>Email upload requests</h2>
          <div>
            <button onClick={() => previewRequestEmail(null)} style={{
            marginRight: 8
          }} title="Exactly what a branch receives. No email is sent and no upload link is issued.">
              Preview email
            </button>
            <button onClick={onClose}>Close</button>
          </div>
        </div>

        {requestPreview && <div style={{
        marginTop: 10,
        border: "1px solid #ccc"
      }}>
            <div style={{
          background: "#eee",
          padding: "6px 10px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
              <strong>{requestPreview.subject}</strong>
              <button onClick={() => setRequestPreview(null)}>Hide</button>
            </div>
            {!requestPreview.hasScreenshot && <div style={{
          background: "#fff8e1",
          borderBottom: "1px solid #e6d28a",
          padding: "6px 10px",
          fontSize: "0.85em",
          color: "#7a5c00"
        }}>
                No ProScript screenshot is installed on the server, so the picture step
                is missing from the email. Put the image in the backend&rsquo;s
                <code> assets/proscript_report_settings.png</code>.
              </div>}
            {/* srcDoc keeps the email's own styling out of this page */}
            <iframe title="request preview" srcDoc={requestPreview.html} style={{
          width: "100%",
          height: "440px",
          border: 0
        }} />
          </div>}

        <div style={{
        background: "#f6f8fa",
        border: "1px solid #dfe3e8",
        padding: "8px 10px",
        margin: "10px 0",
        fontSize: "0.9em",
        lineHeight: 1.45
      }}>
          Each branch gets the ProScript export instructions and a link that uploads
          straight into that branch &mdash; no login, and no pharmacy to choose, so
          nobody can file their figures against the wrong shop. Links last 30 days.
          <div style={{
          marginTop: 6,
          color: "#555"
        }}>
            Addresses are held against the branch here and can be edited. Where a
            branch had none, its login address has been filled in as a starting
            point &mdash; editing it here does not change the branch&rsquo;s login.
          </div>
        </div>

        {recipientsBusy && <div>Loading branches&hellip;</div>}

        {!recipientsBusy && recipients && recipients.length > 0 && <table border="1" cellPadding="6" style={{
        width: "100%",
        fontSize: "0.9em"
      }}>
            <thead style={{
          position: "sticky",
          top: 0,
          background: "#eee"
        }}>
              <tr>
                <th style={{
              textAlign: "left"
            }}>Branch</th>
                <th style={{
              textAlign: "left",
              minWidth: 280
            }}>Email address</th>
                <th style={{
              textAlign: "left"
            }}>Last sent</th>
                <th style={{
              textAlign: "left"
            }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map(m => {
            const dirty = isDirty(m);
            const saving = !!savingEmail[m.pharmacyId];
            return <tr key={m.pharmacyId} style={{
              background: m.email ? undefined : "#fff4f4"
            }}>
                    <td>{displayName(m.pharmacyName, masked)}</td>
                    <td>
                      <div style={{
                  display: "flex",
                  gap: 6,
                  alignItems: "center"
                }}>
                        <input type="email" value={drafts[m.pharmacyId] ?? ""} placeholder="no address - type one and save" disabled={saving} onChange={e => setDrafts(t => ({
                    ...t,
                    [m.pharmacyId]: e.target.value
                  }))} onKeyDown={e => {
                    if (e.key === "Enter" && dirty) saveEmail(m);
                  }} style={{
                    flex: 1,
                    minWidth: 0,
                    padding: "4px 6px",
                    border: dirty ? "1px solid #1876D2" : "1px solid #ccc"
                  }} />
                        {/* Only offered when there is something to save. A Save
                            button that is always live invites a click that does
                            nothing and teaches people the button does nothing. */}
                        {dirty && <>
                            <button onClick={() => saveEmail(m)} disabled={saving} title="Save this address against the branch">
                              {saving ? "Saving..." : "Save"}
                            </button>
                            <button onClick={() => setDrafts(t => ({
                      ...t,
                      [m.pharmacyId]: m.email || ""
                    }))} disabled={saving} title="Put it back to the saved address">
                              Undo
                            </button>
                          </>}
                      </div>
                      {!m.email && !dirty && <div style={{
                  color: "#c00",
                  fontSize: "0.85em",
                  marginTop: 3
                }}>
                          Nowhere to send to
                        </div>}
                      {m.emailSource === "seeded" && !dirty && <div style={{
                  color: "#7a5c00",
                  fontSize: "0.85em",
                  marginTop: 3
                }}>
                          Taken from this branch&rsquo;s login &mdash; worth checking
                        </div>}
                    </td>
                    <td style={{
                whiteSpace: "nowrap",
                fontSize: "0.9em",
                color: "#555"
              }}>
                      {m.lastSentAt ? new Date(m.lastSentAt).toLocaleDateString("en-GB") : "\u2014"}
                      {m.uploads > 0 && <div style={{
                  fontSize: "0.85em",
                  color: "#2e7d32"
                }}>
                          {m.uploads} upload{m.uploads === 1 ? "" : "s"} since
                        </div>}
                    </td>
                    <td style={{
                whiteSpace: "nowrap"
              }}>
                      <button onClick={() => previewRequestEmail(m)} style={{
                  marginRight: 6
                }} title="Preview the email as this branch would receive it">
                        Preview
                      </button>
                      {/* Blocked while an edit is unsaved. The send uses the
                          saved address, so sending mid-edit would go somewhere
                          other than what the box on screen says. */}
                      <button disabled={!m.email || dirty || saving || !!sendingTo[m.pharmacyId]} title={dirty ? "Save the address first - the send would use the old one" : m.email ? `Send the upload request to ${m.email}` : "There is nowhere to send it until this branch has an email address"} onClick={() => sendRequestTo(m)}>
                        {sendingTo[m.pharmacyId] ? "Sending..." : "Send email"}
                      </button>
                      {/* Sent in THIS dialogue only. A tick that survived a reload
                          would claim more than it knows - the "Last sent" column
                          is the durable record. Kept beside the button rather
                          than in a column of its own, which sat empty. */}
                      {sentTo[m.pharmacyId] && <span style={{
                  marginLeft: 8,
                  color: "#2e7d32",
                  fontSize: "1.1em"
                }} title="Sent just now">&#10003;</span>}
                    </td>
                  </tr>;
          })}
            </tbody>
          </table>}

        {!recipientsBusy && recipients && recipients.length === 0 && <div style={{
        color: "#777"
      }}>No member branches on this organisation.</div>}

        <div style={{
        textAlign: "right",
        marginTop: "12px"
      }}>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>;
}
